import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

export const LocationFeatures = {
  UnsafeSorting: 'unsafe-sorting',
  FakeSparseResponseData: 'fake-sparse-response-data',
  SynthSparseResponseData: 'synth-sparse-response-data',
  MoreDevSettings: 'more-dev-settings',
} as const

export const LocationFeatureEnabled = 'yes'
export const LocationFeatureDisabled = 'no'

type LocationFeaturesType = typeof LocationFeatures
export type LocationFeatureKey = LocationFeaturesType[keyof LocationFeaturesType]

/** A hook similar to useSessionFeatures,
 * but for features given in the URL that are not stored in session storage,
 * primarily for special purposes (e.g. logic testing/demonstration);
 * I.e. feature states you really don't want to store, like, for example
 * UnsafeSorting (sort on props that might be nullish)
 * combined with
 * FakeSparseResponseData (transforming data into having decreased integrity) or SynthSparseResponseData,
 * meant to demonstrate app crashing (or undesired behaviour)
 * unless using the safe/default logic. */
export const useLocationFeatures = () => {
  const location = useLocation()
  const params = useMemo(() => new URLSearchParams(location.search), [location.search])

  const features = useMemo(() => {
    return Object.values(LocationFeatures).filter(
      key => params.get(key) && params.get(key)?.toLowerCase() === LocationFeatureEnabled
    ) as LocationFeatureKey[]
  }, [params])

  const hasFeature = (feature: LocationFeatureKey) => features.includes(feature)

  return { features, hasFeature }
}

export const useLocationFeature = (feature: LocationFeatureKey) => {
  const { hasFeature } = useLocationFeatures()
  return hasFeature(feature)
}
